import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Play, Power, Clock, TrendingUp, Search, FileText, Plus, Star, Trash2, Sparkles, Globe, HelpCircle, Users } from 'lucide-react'
import { useArticlesStore } from '@/lib/stores/articlesStore'
import { Button, Card, CardContent, CardHeader, CardTitle, Badge, Spinner, Input } from '@/components/ui'
import { formatDateTime } from '@/lib/utils'
import { api } from '@/lib/api/client'
import type { Proposal } from '@/types'

interface ArticleKeyword {
  id: string
  keyword: string
  source: 'manual' | 'ai_suggested' | 'serpapi_related'
  is_primary: number
  serp_position: number | null
  last_checked_at: string | null
}

interface SerpData {
  keyword: string
  position?: number
  positionMatchType?: 'exact' | 'domain'
  positionMatchedUrl?: string
  organic_results: Array<{
    position: number
    title: string
    link: string
    snippet: string
  }>
  people_also_ask: Array<{
    question: string
    snippet?: string
  }>
  related_searches: string[]
}

export function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { selectedArticle, isLoading, fetchArticle, triggerAnalysis } = useArticlesStore()
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [keywords, setKeywords] = useState<ArticleKeyword[]>([])
  const [newKeyword, setNewKeyword] = useState('')
  const [isAddingKeyword, setIsAddingKeyword] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [serpData, setSerpData] = useState<SerpData | null>(null)

  const fetchKeywords = async () => {
    if (!id) return
    const res = await api.get<{ keywords: ArticleKeyword[] }>(`/articles/${id}/keywords`)
    if (res.success && res.data?.keywords) {
      setKeywords(res.data.keywords)
    }
  }

  useEffect(() => {
    if (id) {
      fetchArticle(id)
      fetchKeywords()
      api.get<{ proposals: (Proposal & { serp_data: SerpData | null })[] }>(`/proposals?article_id=${id}`).then(res => {
        if (res.success && res.data?.proposals) {
          setProposals(res.data.proposals)
          // Get serp_data from latest proposal
          const latestWithSerp = res.data.proposals.find(p => p.serp_data)
          if (latestWithSerp?.serp_data) {
            setSerpData(latestWithSerp.serp_data)
          }
        }
      })
    }
  }, [id, fetchArticle])

  const handleToggleCron = async () => {
    if (!selectedArticle) return
    await api.put(`/articles/${selectedArticle.id}`, {
      cron_enabled: !selectedArticle.cron_enabled,
    })
    fetchArticle(selectedArticle.id)
  }

  const handleAnalyze = async () => {
    if (!selectedArticle) return
    setIsAnalyzing(true)
    const proposalId = await triggerAnalysis(selectedArticle.id)
    setIsAnalyzing(false)
    if (proposalId) {
      navigate(`/proposals/${proposalId}`)
    }
    // Refresh keywords after analysis (may have new related searches)
    fetchKeywords()
  }

  const handleAddKeyword = async () => {
    if (!id || !newKeyword.trim()) return
    setIsAddingKeyword(true)
    await api.post(`/articles/${id}/keywords`, {
      keyword: newKeyword.trim(),
      isPrimary: keywords.length === 0
    })
    setNewKeyword('')
    setIsAddingKeyword(false)
    fetchKeywords()
  }

  const handleSetPrimary = async (keywordId: string) => {
    if (!id) return
    await api.put(`/articles/${id}/keywords`, { keywordId })
    fetchKeywords()
    fetchArticle(id)
  }

  const handleDeleteKeyword = async (keywordId: string) => {
    if (!id) return
    await api.delete(`/articles/${id}/keywords?keyword_id=${keywordId}`)
    fetchKeywords()
  }

  const handleGenerateKeywords = async () => {
    if (!id) return
    setIsGenerating(true)
    await api.post(`/articles/${id}/suggest-keywords`, {})
    setIsGenerating(false)
    fetchKeywords()
    fetchArticle(id)
  }

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'ai_suggested':
        return <Sparkles className="w-3 h-3" />
      case 'serpapi_related':
        return <Globe className="w-3 h-3" />
      default:
        return null
    }
  }

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'ai_suggested':
        return 'AI'
      case 'serpapi_related':
        return 'SERP'
      default:
        return 'Manual'
    }
  }

  if (isLoading || !selectedArticle) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/articles"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna alla lista
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {selectedArticle.title}
            </h1>
            <div className="flex items-center gap-4 mt-2">
              <Badge>{selectedArticle.type}</Badge>
              <a
                href={selectedArticle.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                {selectedArticle.url}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant={selectedArticle.cron_enabled ? 'danger' : 'secondary'}
              onClick={handleToggleCron}
            >
              <Power className="w-4 h-4 mr-2" />
              {selectedArticle.cron_enabled ? 'Disattiva cron' : 'Attiva cron'}
            </Button>
            <Button onClick={handleAnalyze} isLoading={isAnalyzing}>
              <Play className="w-4 h-4 mr-2" />
              Analizza ora
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Posizione SERP</p>
                <p className="text-xl font-semibold">
                  {selectedArticle.serp_position
                    ? `#${selectedArticle.serp_position}`
                    : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Ultima analisi</p>
                <p className="text-xl font-semibold">
                  {formatDateTime(selectedArticle.last_analyzed_at)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Power className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Cron mensile</p>
                <p className="text-xl font-semibold">
                  {selectedArticle.cron_enabled ? 'Attivo' : 'Disattivo'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Keywords */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Search className="w-5 h-5 text-gray-500" />
              <CardTitle>Keyword Tracciate</CardTitle>
              <Badge variant="default">{keywords.length}</Badge>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleGenerateKeywords}
              isLoading={isGenerating}
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Genera
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Add keyword form */}
          <div className="flex gap-2 mb-4">
            <Input
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              placeholder="Aggiungi keyword..."
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
            />
            <Button onClick={handleAddKeyword} disabled={!newKeyword.trim() || isAddingKeyword}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Keywords list */}
          {keywords.length === 0 ? (
            <p className="text-gray-500 text-center py-4 text-sm">
              Nessuna keyword. Aggiungi manualmente o esegui "Analizza ora" per suggerimenti AI.
            </p>
          ) : (
            <div className="space-y-2">
              {keywords.map((kw) => (
                <div
                  key={kw.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    kw.is_primary ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {kw.is_primary ? (
                      <Star className="w-4 h-4 text-primary-500 fill-primary-500" />
                    ) : (
                      <button
                        onClick={() => handleSetPrimary(kw.id)}
                        className="text-gray-300 hover:text-primary-500"
                        title="Imposta come primaria"
                      >
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    <div>
                      <span className="font-medium">{kw.keyword}</span>
                      {kw.serp_position && (
                        <span className="ml-2 text-sm text-green-600 font-semibold">
                          #{kw.serp_position}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="default" className="text-xs flex items-center gap-1">
                      {getSourceIcon(kw.source)}
                      {getSourceLabel(kw.source)}
                    </Badge>
                    <button
                      onClick={() => handleDeleteKeyword(kw.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {keywords.length > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              <Star className="w-3 h-3 inline text-primary-500 fill-primary-500" /> = Keyword primaria usata per analisi SERP
            </p>
          )}
        </CardContent>
      </Card>

      {/* SERP Analysis */}
      {serpData && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-gray-500" />
                <CardTitle>Analisi SERP</CardTitle>
                {serpData.keyword && (
                  <Badge variant="info">"{serpData.keyword}"</Badge>
                )}
              </div>
              <Link to={`/articles/${id}/serp`}>
                <Button variant="secondary" size="sm">
                  <Search className="w-4 h-4 mr-1" />
                  Analisi completa
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Position */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <p className="text-4xl font-bold text-primary-600">
                  {serpData.position ? `#${serpData.position}` : '-'}
                </p>
                <p className="text-sm text-gray-500">Posizione</p>
              </div>
              {serpData.positionMatchType && (
                <div>
                  <Badge variant={serpData.positionMatchType === 'exact' ? 'success' : 'warning'}>
                    {serpData.positionMatchType === 'exact' ? 'URL esatto' : 'Altro URL del dominio'}
                  </Badge>
                  {serpData.positionMatchedUrl && serpData.positionMatchType === 'domain' && (
                    <p className="text-xs text-gray-500 mt-1 truncate max-w-md">
                      {serpData.positionMatchedUrl}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Competitors */}
            {serpData.organic_results && serpData.organic_results.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-gray-500" />
                  <h4 className="font-medium">Top 10 Competitor</h4>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {serpData.organic_results.slice(0, 10).map((result, i) => (
                    <div key={i} className="flex items-start gap-3 p-2 border rounded text-sm">
                      <span className="font-bold text-gray-400 w-6">#{result.position}</span>
                      <div className="flex-1 min-w-0">
                        <a
                          href={result.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-primary-600 hover:underline line-clamp-1"
                        >
                          {result.title}
                        </a>
                        <p className="text-gray-500 line-clamp-2">{result.snippet}</p>
                        <p className="text-xs text-gray-400 truncate">{result.link}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* People Also Ask */}
            {serpData.people_also_ask && serpData.people_also_ask.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <HelpCircle className="w-4 h-4 text-gray-500" />
                  <h4 className="font-medium">People Also Ask</h4>
                  <Badge variant="default">{serpData.people_also_ask.length}</Badge>
                </div>
                <div className="space-y-2">
                  {serpData.people_also_ask.map((paa, i) => (
                    <div key={i} className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                      <p className="font-medium text-blue-900">{paa.question}</p>
                      {paa.snippet && (
                        <p className="text-sm text-blue-700 mt-1">{paa.snippet}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related Searches */}
            {serpData.related_searches && serpData.related_searches.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Search className="w-4 h-4 text-gray-500" />
                  <h4 className="font-medium">Ricerche Correlate</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {serpData.related_searches.map((search, i) => (
                    <Badge key={i} variant="default" className="text-sm">
                      {search}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* No SERP data message */}
      {!serpData && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-gray-500" />
              <CardTitle>Analisi SERP</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 text-center py-4">
              Nessun dato SERP. Clicca "Analizza ora" per ottenere dati da Google.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Proposals history */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500" />
            <CardTitle>Proposte recenti</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {proposals.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Nessuna proposta ancora. Clicca "Analizza ora" per generare la prima proposta.
            </p>
          ) : (
            <div className="space-y-3">
              {proposals.map((proposal) => (
                <Link
                  key={proposal.id}
                  to={`/proposals/${proposal.id}`}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <p className="font-medium">Proposta #{proposal.id.slice(0, 8)}</p>
                    <p className="text-sm text-gray-500">{formatDateTime(proposal.created_at)}</p>
                  </div>
                  <Badge
                    variant={
                      proposal.status === 'pending_inputs' ? 'warning' :
                      proposal.status === 'ready' ? 'info' :
                      proposal.status === 'applied' ? 'success' : 'danger'
                    }
                  >
                    {proposal.status === 'pending_inputs' ? 'Risposte richieste' :
                     proposal.status === 'ready' ? 'Pronta' :
                     proposal.status === 'applied' ? 'Applicata' : 'Scartata'}
                  </Badge>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
